// Webinar metric targets: the cold / warm traffic benchmark ranges shown on the Webinars page (list and card
// views) and, from Oct 19, 2026, on Dashboard → Velocity Metrics' Webinar section (Joey Forslund: "include all of
// those metrics in the velocity metrics on the dashboard for the webinar funnel and then those are our target
// metrics"). One source for both pages, so a target changed here changes everywhere.
//
// Units
// - "%", "$", "min": a fixed range the metric's own value is compared with.
// - "pct_of_optins": a count target expressed as a share of the webinar's opt-ins (e.g. Total Apps 18–25% of
//   opt-ins). It becomes a count range for whatever opt-ins sit in the same view: round(opt-ins × lo%) to
//   round(opt-ins × hi%). One webinar = that webinar's opt-ins; several webinars = their opt-ins added.
//
// Indicator (same on both pages): above the range = green ↗, inside = yellow ✓, below = red ↘.

export type WebinarAudience = "cold" | "warm";
export type WebinarTargetRange = { lo: number; hi: number; unit: "%" | "$" | "min" | "pct_of_optins" };
export type WebinarTargetMap = Record<string, { cold: WebinarTargetRange; warm: WebinarTargetRange }>;
export type WebinarTargetIndicator = "above" | "in" | "below";

export const WEBINAR_SALES_TARGETS: WebinarTargetMap = {
  "Opt-in Rate":      { cold: { lo: 20, hi: 30, unit: "%" },            warm: { lo: 40, hi: 55, unit: "%" } },
  "Show Rate":        { cold: { lo: 60, hi: 70, unit: "%" },            warm: { lo: 70, hi: 85, unit: "%" } },
  "Close Rate":       { cold: { lo: 25, hi: 35, unit: "%" },            warm: { lo: 35, hi: 50, unit: "%" } },
  "Full App→Booking": { cold: { lo: 35, hi: 55, unit: "%" },            warm: { lo: 45, hi: 70, unit: "%" } },
  "Optin→Booking":    { cold: { lo: 4, hi: 8, unit: "%" },              warm: { lo: 8, hi: 15, unit: "%" } },
  "Opt-in→Full App":  { cold: { lo: 12, hi: 20, unit: "%" },            warm: { lo: 20, hi: 35, unit: "%" } },
  "Optin→Qual App":   { cold: { lo: 6, hi: 12, unit: "%" },             warm: { lo: 12, hi: 20, unit: "%" } },
  "Qual→Booking":     { cold: { lo: 40, hi: 60, unit: "%" },            warm: { lo: 50, hi: 70, unit: "%" } },
  "Add to Calendar":  { cold: { lo: 20, hi: 35, unit: "%" },            warm: { lo: 35, hi: 55, unit: "%" } },
  "Joined WhatsApp":  { cold: { lo: 25, hi: 40, unit: "%" },            warm: { lo: 40, hi: 60, unit: "%" } },
  "Total Apps":       { cold: { lo: 18, hi: 25, unit: "pct_of_optins" }, warm: { lo: 30, hi: 45, unit: "pct_of_optins" } },
  "Full Apps":        { cold: { lo: 12, hi: 20, unit: "pct_of_optins" }, warm: { lo: 20, hi: 35, unit: "pct_of_optins" } },
  "Qualified Apps":   { cold: { lo: 6, hi: 12, unit: "pct_of_optins" },  warm: { lo: 12, hi: 20, unit: "pct_of_optins" } },
  "Calls Booked":     { cold: { lo: 4, hi: 8, unit: "pct_of_optins" },   warm: { lo: 8, hi: 15, unit: "pct_of_optins" } },
};

export const WEBINAR_ON_WEBINAR_TARGETS: WebinarTargetMap = {
  "Attended":         { cold: { lo: 20, hi: 30, unit: "pct_of_optins" }, warm: { lo: 35, hi: 50, unit: "pct_of_optins" } },
  "Show Rate":        { cold: { lo: 20, hi: 30, unit: "%" },              warm: { lo: 35, hi: 50, unit: "%" } },
  "Avg Duration":     { cold: { lo: 60, hi: 90, unit: "min" },            warm: { lo: 70, hi: 100, unit: "min" } },
  "@ Pitch":          { cold: { lo: 70, hi: 85, unit: "%" },              warm: { lo: 80, hi: 90, unit: "%" } },
  "Booking Rate":     { cold: { lo: 20, hi: 30, unit: "%" },              warm: { lo: 30, hi: 50, unit: "%" } },
  "Total App Rate":   { cold: { lo: 40, hi: 60, unit: "%" },              warm: { lo: 50, hi: 70, unit: "%" } },
  "Full App Rate":    { cold: { lo: 30, hi: 45, unit: "%" },              warm: { lo: 40, hi: 60, unit: "%" } },
  "Qual App Rate":    { cold: { lo: 15, hi: 25, unit: "%" },              warm: { lo: 25, hi: 40, unit: "%" } },
  "Pitch Start":      { cold: { lo: 60, hi: 90, unit: "min" },            warm: { lo: 60, hi: 100, unit: "min" } },
};

export const WEBINAR_ZOOM_TARGETS: WebinarTargetMap = {
  "Attended":      { cold: { lo: 20, hi: 30, unit: "pct_of_optins" }, warm: { lo: 35, hi: 50, unit: "pct_of_optins" } },
  "Show Rate":     { cold: { lo: 20, hi: 30, unit: "%" },             warm: { lo: 35, hi: 50, unit: "%" } },
  "Avg Duration":  { cold: { lo: 60, hi: 90, unit: "min" },           warm: { lo: 70, hi: 100, unit: "min" } },
  "Day of":        { cold: { lo: 22, hi: 35, unit: "%" },             warm: { lo: 35, hi: 55, unit: "%" } },
  "1d before":     { cold: { lo: 18, hi: 28, unit: "%" },             warm: { lo: 30, hi: 45, unit: "%" } },
  "2d before":     { cold: { lo: 15, hi: 22, unit: "%" },             warm: { lo: 25, hi: 35, unit: "%" } },
  "3d before":     { cold: { lo: 8, hi: 12, unit: "%" },              warm: { lo: 12, hi: 18, unit: "%" } },
  "4d before":     { cold: { lo: 7, hi: 10, unit: "%" },              warm: { lo: 10, hi: 16, unit: "%" } },
  "5d before":     { cold: { lo: 6, hi: 9, unit: "%" },               warm: { lo: 9, hi: 14, unit: "%" } },
  "6d before":     { cold: { lo: 5, hi: 8, unit: "%" },               warm: { lo: 8, hi: 12, unit: "%" } },
  "7d before":     { cold: { lo: 5, hi: 8, unit: "%" },               warm: { lo: 8, hi: 12, unit: "%" } },
};

export type ResolvedWebinarTarget = { rangeStr: string; indicator: WebinarTargetIndicator; lo: number; hi: number };

/**
 * The target range and up/down indicator for one webinar metric. `optins` = the opt-ins in the same view (used
 * by "pct_of_optins" targets). Returns null when the metric has no target or there is nothing to compare.
 */
export function resolveWebinarTarget(label: string, rawValue: number | null | undefined, targets: WebinarTargetMap, audience: WebinarAudience, optins: number): ResolvedWebinarTarget | null {
  const target = targets[label];
  if (!target || rawValue == null) return null;
  const range = audience === "warm" ? target.warm : target.cold;
  const { lo, hi, unit } = range;
  if (unit === "pct_of_optins") {
    if (!optins) return null;
    const loCount = Math.round(optins * lo / 100);
    const hiCount = Math.round(optins * hi / 100);
    return { rangeStr: `${loCount}–${hiCount}`, indicator: rawValue >= loCount && rawValue <= hiCount ? "in" : rawValue > hiCount ? "above" : "below", lo: loCount, hi: hiCount };
  }
  const rangeStr = unit === "$"
    ? (lo >= 1000 ? `$${(lo / 1000).toFixed(0)}K–$${(hi / 1000).toFixed(0)}K` : `$${lo}–$${hi}`)
    : unit === "min" ? `${lo}–${hi}m` : `${lo}–${hi}%`;
  return { rangeStr, indicator: rawValue >= lo && rawValue <= hi ? "in" : rawValue > hi ? "above" : "below", lo, hi };
}
